import type { AuditTrailEvent } from '@api/devices/contracts';

const MISSING = 'Not recorded';
/** Bound all stored strings, including metadata. React renders the result as text. */
export function recordedText(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return MISSING;
  return value.length > 2000 ? `${value.slice(0, 2000)}…` : value;
}
export function recordedTime(at: string): string {
  const date = new Date(at);
  return Number.isFinite(date.getTime()) ? `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC` : MISSING;
}
interface Field { label: string; value: string }
const state = (value: unknown) => value === 'failed' || value === 'unknown_outcome' ? value : MISSING;
const onOff = (value: unknown) => typeof value === 'boolean' ? value ? 'On' : 'Off' : MISSING;

/** Deliberately no generic details renderer. Add fields only against their recorded action's contract. */
export function auditDetailFields(e: AuditTrailEvent): Field[] {
  switch (e.action) {
    case 'job.checked': return [{ label: 'Job state', value: state(e.details.state) }, { label: 'Check note', value: recordedText(e.details.note) }];
    // uncheck records no note: it records whose earlier check was taken back, without a principal lookup.
    case 'job.check_undone': return [{ label: 'Job state', value: state(e.details.state) }, { label: 'Previous check by', value: recordedText(e.details.checkedBy) }];
    case 'job.closed': return [{ label: 'Job state', value: state(e.details.state) }];
    case 'organization.automatic_updates_changed': return [
      { label: 'Automatic updates before', value: onOff(e.details.before) }, { label: 'Automatic updates after', value: onOff(e.details.after) },
      ...(e.actorType === 'agent' ? [{ label: 'Approved by', value: recordedText(e.details.approvedBy) },
        { label: 'Elevation ID', value: recordedText(e.details.elevationId) }] : []),
    ];
    case 'agent_elevation.requested':
    case 'agent_elevation.approved': return [
      { label: 'Permission', value: recordedText(e.details.scope) },
      { label: 'Customer IDs', value: Array.isArray(e.details.customerIds) && e.details.customerIds.every(id => typeof id === 'string')
        ? recordedText(e.details.customerIds.join(', ')) : MISSING },
      ...(e.action === 'agent_elevation.approved' ? [{ label: 'Access expires', value: typeof e.details.expiresAt === 'string'
        ? recordedTime(e.details.expiresAt) : MISSING }] : []),
    ];
    case 'agent_elevation.declined':
    case 'agent_elevation.revoked': return [{ label: 'Agent credential ID', value: recordedText(e.details.credentialId) }];
    default: return [];
  }
}
const uuid = (value: string | null): value is string => !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
export function auditTarget(e: AuditTrailEvent, mspId: string): { label: string; href?: string } {
  const label = recordedText(e.targetId);
  // Only the trail's joined, visible device proves a current device/job route is available.
  if (!e.device || !uuid(e.device.id) || !uuid(e.targetId)) return { label };
  const base = `/w/${encodeURIComponent(mspId)}`;
  if (e.targetType === 'device' && e.targetId === e.device.id) return { label, href: `${base}/devices/${e.device.id}` };
  if (e.targetType === 'job') return { label, href: `${base}/work?job=${e.targetId}` };
  return { label };
}
